import React, { useRef, useState, useEffect } from "react";
import type { Course } from "@/types/courses";
import { useUserCourseSettings } from "@/hooks/useUserCourseSettings";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ImagePlus, Trash2, Upload, Plus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { EmojiPicker } from "@/components/ui/emoji-picker/EmojiPicker";

export type CourseTab = "course" | "sources";

interface CourseHeroProps {
  course: Course;
  activeTab: CourseTab;
  onTabChange: (tab: CourseTab) => void;
}

export const CourseHero: React.FC<CourseHeroProps> = ({ course, activeTab, onTabChange }) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { settings, uploadCover, uploadIcon, removeCover, removeIcon, upsert } =
    useUserCourseSettings(course.id);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [iconPopoverOpen, setIconPopoverOpen] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(course.title);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleSave = async () => {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === course.title) {
      setEditTitle(course.title);
      setIsEditingTitle(false);
      return;
    }
    const { error } = await supabase
      .from("courses")
      .update({ title: trimmed })
      .eq("id", course.id);
    if (error) {
      toast({ title: "Failed to update title", description: error.message, variant: "destructive" });
      setEditTitle(course.title);
    } else {
      toast({ title: "Title updated" });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    }
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditTitle(course.title);
    setIsEditingTitle(false);
  };

  // Optimistic local overrides
  const [localCover, setLocalCover] = useState<string | null | undefined>(undefined);
  const [localIcon, setLocalIcon] = useState<string | null | undefined>(undefined);

  const canEdit = !!user;

  // Merge: local optimistic > user settings > base course > fallback
  const coverUrl =
    localCover !== undefined
      ? localCover
      : settings?.coverImageUrl ?? course.coverImageUrl ?? course.coverUrl;

  const iconUrl =
    localIcon !== undefined
      ? localIcon
      : settings?.iconUrl ?? course.iconUrl;

  const iconEmoji = settings?.iconEmoji ?? course.iconEmoji;
  const hasIcon = !!iconUrl || !!iconEmoji;

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadCover(file);
      if (url) setLocalCover(url);
      toast({ title: "Cover updated" });
    } catch {
      setLocalCover(undefined);
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const handleCoverRemove = async () => {
    setLocalCover(null);
    try {
      await removeCover();
      toast({ title: "Cover removed" });
    } catch {
      setLocalCover(undefined);
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconUploading(true);
    setIconPopoverOpen(false);
    try {
      const url = await uploadIcon(file);
      if (url) setLocalIcon(url);
      toast({ title: "Icon updated" });
    } catch {
      setLocalIcon(undefined);
    } finally {
      setIconUploading(false);
      if (iconInputRef.current) iconInputRef.current.value = "";
    }
  };

  const handleIconRemove = async () => {
    setIconPopoverOpen(false);
    setLocalIcon(null);
    try {
      await removeIcon();
      toast({ title: "Icon removed" });
    } catch {
      setLocalIcon(undefined);
    }
  };

  const handleEmojiSelect = async (emoji: string) => {
    setIconPopoverOpen(false);
    setLocalIcon(null);
    try {
      await upsert({ iconEmoji: emoji, iconUrl: null });
      toast({ title: "Icon updated" });
    } catch {
      setLocalIcon(undefined);
    }
  };

  return (
    <div className="w-full">
      {/* Hidden file inputs */}
      <input
        ref={coverInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCoverUpload}
      />
      <input
        ref={iconInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleIconUpload}
      />

      {/* Cover banner + icon wrapper */}
      <div className="relative">
        <div
          className="group relative h-32 sm:h-40 lg:h-48 w-full overflow-hidden"
          style={{
            backgroundImage: coverUrl
              ? `url("${coverUrl}")`
              : "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.5) 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/30 to-transparent" />

          {/* Cover edit controls */}
          {canEdit && (
            <div
              className={`absolute top-3 right-3 z-10 flex gap-2 transition-opacity ${
                isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <Button
                size="sm"
                variant="secondary"
                className="h-7 text-xs gap-1.5 shadow-md"
                onClick={() => coverInputRef.current?.click()}
                disabled={coverUploading}
              >
                <ImagePlus className="h-3.5 w-3.5" />
                {coverUploading ? "Uploading…" : "Change cover"}
              </Button>
              {coverUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs gap-1.5 bg-background/60 backdrop-blur-sm shadow-md hover:bg-destructive/10 hover:text-destructive"
                  onClick={handleCoverRemove}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Icon – sits outside overflow-hidden so it stacks on top */}
        <div className="absolute bottom-0 left-6 translate-y-1/2 z-20">
          {hasIcon ? (
            <Popover open={iconPopoverOpen} onOpenChange={setIconPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  className="rounded-xl border-2 border-background bg-card shadow-md p-2 cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all"
                  disabled={!canEdit}
                >
                  {iconUrl ? (
                    <img
                      src={iconUrl}
                      alt=""
                      className="h-8 w-8 rounded-lg object-cover"
                    />
                  ) : iconEmoji ? (
                    <span className="h-8 w-8 flex items-center justify-center text-2xl">
                      {iconEmoji}
                    </span>
                  ) : (
                    <div
                      className="h-8 w-8 rounded-lg"
                      style={{
                        background: course.accentColor
                          ? course.accentColor
                          : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.5))",
                      }}
                    />
                  )}
                </button>
              </PopoverTrigger>
              {canEdit && (
                <PopoverContent
                  className={`p-3 ${isMobile ? "w-[calc(100vw-2rem)]" : "w-72"}`}
                  align="start"
                  side="bottom"
                >
                  <div className="flex flex-col gap-2">
                    <EmojiPicker onSelect={handleEmojiSelect} />
                    <div className="border-t border-border pt-2 flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start gap-2 text-sm"
                        onClick={() => {
                          iconInputRef.current?.click();
                        }}
                        disabled={iconUploading}
                      >
                        <Upload className="h-4 w-4" />
                        {iconUploading ? "Uploading…" : "Upload image"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start gap-2 text-sm text-destructive hover:text-destructive"
                        onClick={handleIconRemove}
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              )}
            </Popover>
          ) : canEdit ? (
            <Popover open={iconPopoverOpen} onOpenChange={setIconPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  className="rounded-xl border-2 border-dashed border-muted-foreground/30 bg-card/80 shadow-sm p-2 cursor-pointer hover:border-primary/50 transition-colors"
                >
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center">
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent
                className={`p-3 ${isMobile ? "w-[calc(100vw-2rem)]" : "w-72"}`}
                align="start"
                side="bottom"
              >
                <div className="flex flex-col gap-2">
                  <EmojiPicker onSelect={handleEmojiSelect} />
                  <div className="border-t border-border pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start gap-2 text-sm w-full"
                      onClick={() => {
                        setIconPopoverOpen(false);
                        iconInputRef.current?.click();
                      }}
                      disabled={iconUploading}
                    >
                      <Upload className="h-4 w-4" />
                      {iconUploading ? "Uploading…" : "Upload image"}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="rounded-xl border-2 border-background bg-card shadow-md p-2">
              <div
                className="h-8 w-8 rounded-lg"
                style={{
                  background: course.accentColor
                    ? course.accentColor
                    : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.5))",
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Title + description */}
      <div className="px-4 sm:px-6 pt-8 pb-4">
        <div className="flex items-start gap-3 my-[12px]">
          <div className="flex-1 min-w-0 py-0">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleTitleSave();
                  if (e.key === "Escape") handleTitleCancel();
                }}
                onBlur={handleTitleCancel}
                className="text-2xl sm:text-3xl font-medium text-foreground leading-tight bg-transparent outline-none w-full"
              />
            ) : (
              <h1
                className={`text-2xl sm:text-3xl font-medium text-foreground leading-tight ${canEdit ? "cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors" : ""}`}
                onClick={() => {
                  if (canEdit) {
                    setEditTitle(course.title);
                    setIsEditingTitle(true);
                  }
                }}
              >
                {course.title}
              </h1>
            )}
            {course.description && (
              <p className="mt-1.5 text-muted-foreground leading-relaxed line-clamp-2 text-base font-extralight">
                {course.description}
              </p>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="w-full font-medium">
          <div className="mt-4 flex items-center border-b border-border/60">
            <div className="flex items-center text-sm">
              <button
                className={`relative h-8 cursor-pointer overflow-visible pb-2 px-1 mr-4 transition-colors ${
                  activeTab === "course"
                    ? "font-semibold text-foreground"
                    : "font-medium text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => onTabChange("course")}
              >
                <div className="flex items-center gap-2">
                  Course
                  <span className="inline-flex items-center rounded-xl bg-purple-500/10 px-2 py-1 text-xs text-purple-500">
                    Early Access
                  </span>
                </div>
                {activeTab === "course" && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-full" />
                )}
              </button>
              <button
                className={`relative h-8 cursor-pointer overflow-visible pb-2 px-1 transition-colors ${
                  activeTab === "sources"
                    ? "font-semibold text-foreground"
                    : "font-medium text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => onTabChange("sources")}
              >
                Sources
                {activeTab === "sources" && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-full" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
